import { EnvironmentsTabKeys, RepositoriesTabKeys } from './tabs'
import { NONE } from '@/config/ui'

import { Project, ProjectFeatures } from '@/types/index.d'

export function composePathAndParams(
  path: string,
  params: Record<string, unknown>
): string {
  const queryString = mapToQueryString(params)
  return path && queryString?.length > 0 ? [path, queryString].join('?') : path
}

export function mapToQueryString(params: Record<string, unknown>): string {
  return (
    params &&
    Object.entries(params)
      .reduce((accumulator, [key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            if (key === 'sort') {
              value.forEach((curr) =>
                accumulator.push(
                  `${encodeURIComponent(key)}=${encodeURIComponent(curr)}`
                )
              )
            } else {
              accumulator.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(value.join(','))}`
              )
            }
          }
        } else if (value !== null && typeof value !== 'undefined') {
          accumulator.push(
            `${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`
          )
        }
        return accumulator
      }, [])
      .join('&')
  )
}

export function getEmbeddedWebappPath(paths: string | string[]): string {
  const path = Array.isArray(paths) ? paths.join('/') : paths
  return `/workspace/${path}?embedded=true`
}

export function getOpenAndSelectedKeysByPath(
  baseUrl: string,
  isMenuCollapsed: boolean,
  path: string
): {
  openKeys: string[]
  selectedKeys: string[]
} {
  const queryStringIndex = path.indexOf('?')

  const relativePath =
    queryStringIndex > 0 ? path.slice(0, queryStringIndex) : path

  const [rootKey, ...keys] =
    path === baseUrl
      ? ['home']
      : relativePath
          .replace(baseUrl, '')
          .replace('//', '')
          .split('/')
          .filter((str) => /\S/.test(str))
          .reduce((accumulator: string[], str: string) => {
            if (accumulator.length > 0) {
              accumulator.push(`${accumulator[accumulator.length - 1]}-${str}`)
            } else {
              accumulator.push(str)
            }
            return accumulator
          }, [])

  return {
    openKeys: !isMenuCollapsed && keys.length >= 1 ? [rootKey] : [],
    selectedKeys: keys.length >= 1 ? [keys[0]] : [rootKey],
  }
}

export type TabDescriptor = {
  key: keyof typeof RepositoriesTabKeys | keyof typeof EnvironmentsTabKeys
  children?: TabDescriptor[]
  feature?: keyof ProjectFeatures
  icon?: React.ReactNode
  isAvailableForProject?: (project: Project) => boolean
  isHidden?: boolean
  isMemberOnly?: boolean
  isAdminOnly?: boolean
}

export function getCurrentTab(
  path: string[],
  tabs: TabDescriptor[]
): TabDescriptor {
  const parentTab = tabs.find((tab) => tab.key.toLowerCase() === path?.[0])
  if (path?.length && path?.length > 2 && parentTab?.children) {
    const childTab = getLeafTab(parentTab, path, 2)
    if (childTab) {
      return childTab
    }
  }
  return parentTab
}

export function getLeafTab(
  parentTab: TabDescriptor,
  path: string | string[],
  level: number
): TabDescriptor {
  const childTab = parentTab.children.find((tab) => {
    const expected = `${parentTab.key.toLowerCase()}${path?.[level]}`
    return tab.key.toLowerCase() === expected
  })
  return childTab?.children ? getLeafTab(childTab, path, level + 1) : childTab
}

export function formatEmptyArrayAsNoneValue(
  value: string[]
): string[] | string {
  return Array.isArray(value) && value?.length > 0 ? value : NONE
}

export function areValuesDefined(values: unknown[]): boolean {
  for (const value of values) {
    if (typeof value === 'undefined' || value === '' || value === null)
      return false
  }
  return true
}

export function joinValues(...values: string[]): string | null {
  if (!areValuesDefined(values)) return null
  return [...values].join('/')
}

export function parseArrayFromQueryString<T>(value: string): T[] {
  return value?.length && value !== NONE
    ? (value.split(',') as unknown as T[])
    : ([] as T[])
}
